import React, { useState, useEffect } from "react";
import Layout from "../../Layout";
import {
  Button,
  FromToDate,
  Input,
  Select,
  Textarea,
} from "../../components/Form";
import { BiChevronDown, BiPlus } from "react-icons/bi";
import PatientMedicineServiceModal from "../../components/Modals/PatientMedicineServiceModal";
import AddItemModal from "../../components/Modals/AddItemInvoiceModal";
import { sortsDatas } from "../../components/Datas";
import { toast } from "react-hot-toast";
import { BsSend } from "react-icons/bs";
import { IoArrowBackOutline } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { InvoiceProductsTable } from "../../components/Tables";
import SenderReceverComp from "../../components/SenderReceverComp";
import invoiceService from "../../api/invoiceService";

function CreateInvoice() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState([
    new Date(),
    new Date(new Date().setDate(new Date().getDate() + 7)),
  ]);
  const [startDate, endDate] = dateRange;
  const [isOpen, setIsOpen] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);
  const [currency, setCurrency] = useState(sortsDatas.currency[0]);

  // New State
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  // Totals
  const subTotal = items.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const taxAmount = (subTotal * (taxRate / 100));
  const grandTotal = subTotal + taxAmount - discount;

  // date picker
  const onChangeDates = (update) => {
    setDateRange(update);
  };

  const handleAddItem = ({ newItem, quantity }) => {
    // Check if already exists
    const existing = items.find(i => i.id === newItem.id && i.type === newItem.type);
    if (existing) {
      setItems(items.map(i => i.id === newItem.id && i.type === newItem.type ? { ...i, quantity: i.quantity + quantity } : i));
    } else {
      setItems([...items, { ...newItem, quantity }]);
    }
  };

  const handleDeleteItem = (id) => {
    setItems(items.filter(i => i.id !== id));
  };

  const handleSubmit = async () => {
    try {
      if (!selectedPatient) return toast.error("Please select a patient");
      if (items.length === 0) return toast.error("Please add at least one item");

      setLoading(true);
      const payload = {
        patientId: selectedPatient.id,
        items: items.map(i => ({
          item: i.id, // ID of medicine/service
          itemType: i.type, // 'Medicine' or 'Service'
          quantity: i.quantity,
          price: i.price
        })),
        totalAmount: grandTotal,
        paymentDate: startDate,
        dueDate: endDate,
        notes,
        // Backend might need discount/tax fields if schema supports, else just total
      };

      await invoiceService.createInvoice(payload);
      toast.success("Invoice created successfully");
      navigate("/invoices");
    } catch (error) {
      toast.error(error.response?.data?.message || "Creation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {isOpen && (
        <PatientMedicineServiceModal
          closeModal={() => setIsOpen(!isOpen)}
          isOpen={isOpen}
          patient={true}
          onSelect={(patient) => setSelectedPatient(patient)}
        />
      )}
      {itemOpen && (
        <AddItemModal
          closeModal={() => setItemOpen(!itemOpen)}
          isOpen={itemOpen}
          onAddItem={handleAddItem}
        />
      )}
      <div className='flex items-center gap-4'>
        <Link
          to='/invoices'
          className='text-md rounded-lg border border-dashed border-subMain bg-white px-4 py-3'
        >
          <IoArrowBackOutline />
        </Link>
        <h1 className='text-xl font-semibold'>Create Invoice</h1>
      </div>
      <div
        data-aos='fade-up'
        data-aos-duration='1000'
        data-aos-delay='100'
        data-aos-offset='200'
        className='my-8 rounded-xl border-[1px] border-border bg-white p-5'
      >
        {/* header */}
        <div className='grid grid-cols-1 items-center gap-2 sm:grid-cols-2 lg:grid-cols-4'>
          <div className='lg:col-span-3'>
            <img
              src='/images/logo.png'
              alt='logo'
              className='w-32 object-contain'
            />
          </div>

          <div className='flex flex-col gap-4'>
            <FromToDate
              startDate={startDate}
              endDate={endDate}
              label='Dates'
              onChange={onChangeDates}
            />
          </div>
        </div>
        {/* sender and recever */}
        <SenderReceverComp
          item={selectedPatient || { title: "Select Patient" }}
          functions={{
            openModal: () => {
              setIsOpen(!isOpen);
            },
          }}
          button={true}
        />
        {/* products */}
        <div className='mt-8 grid grid-cols-6 gap-6'>
          <div className='col-span-6 overflow-hidden rounded-xl border border-border p-6 lg:col-span-4'>
            <InvoiceProductsTable
              data={items}
              functions={{
                deleteItem: handleDeleteItem,
              }}
              button={true}
            />

            {/* add */}
            <button
              onClick={() => setItemOpen(!itemOpen)}
              className='flex-rows mt-4 w-full gap-2 rounded-lg border border-dashed border-subMain py-4 text-sm text-subMain'
            >
              <BiPlus /> Add Item
            </button>
          </div>
          <div className='col-span-6 flex flex-col gap-6 lg:col-span-2'>
            <Select
              selectedPerson={currency}
              setSelectedPerson={setCurrency}
              datas={sortsDatas?.currency}
            >
              <div className='flex h-14 w-full items-center justify-between rounded-md border border-border px-4 text-xs text-main'>
                <p>{currency?.name}</p>
                <BiChevronDown className='text-xl' />
              </div>
            </Select>
            <div className='grid gap-6 sm:grid-cols-2'>
              <Input
                label='Discount'
                color={true}
                type='number'
                placeholder={"0"}
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value))}
              />
              <Input
                label='Tax(%)'
                color={true}
                type='number'
                placeholder={"0"}
                value={taxRate}
                onChange={(e) => setTaxRate(Number(e.target.value))}
              />
            </div>
            <div className='flex-btn gap-4'>
              <p className='text-sm font-extralight'>Sub Total:</p>
              <h6 className='text-sm font-medium'>{subTotal}</h6>
            </div>
            <div className='flex-btn gap-4'>
              <p className='text-sm font-extralight'>Discount:</p>
              <h6 className='text-sm font-medium'>{discount}</h6>
            </div>
            <div className='flex-btn gap-4'>
              <p className='text-sm font-extralight'>Tax:</p>
              <h6 className='text-sm font-medium'>{taxAmount.toFixed(2)}</h6>
            </div>
            <div className='flex-btn gap-4'>
              <p className='text-sm font-extralight'>Grand Total:</p>
              <h6 className='text-sm font-medium text-green-600'>{grandTotal.toFixed(2)}</h6>
            </div>
            {/* notes */}
            <Textarea
              label='Notes'
              placeholder='Thank you for your business. We hope to work with you again soon!'
              color={true}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {/* button */}
            <Button
              label={loading ? 'Sending...' : 'Save & Send'}
              onClick={handleSubmit}
              Icon={BsSend}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CreateInvoice;
